import { useEffect, useState } from "react";
import { ArrowLeft, Clock, ExternalLink, Heart, Loader2, Minus, Plus, Soup, Star } from "lucide-react";
import type { Ingredient, RecipeDetail as Detail } from "@/types";
import type { Lang } from "@/i18n";
import { T } from "@/i18n";
import { langPath, navigate } from "@/router";
import { DIFFICULTIES } from "@/filters";
import { Button } from "@/components/ui/button";
import { HlsVideo } from "./HlsVideo";
import { LangToggle } from "./LangToggle";
import { useFavorites, useMounted } from "@/favorites";

function difficultyLabel(d: Detail["difficulty"], lang: Lang): string {
  return DIFFICULTIES.find(x => x.value === d)?.label[lang] ?? d;
}

function formatQty(n: number): string {
  return parseFloat(n.toFixed(2)).toString();
}

function ingredientLine(ing: Ingredient, factor: number, imperial: boolean, lang: Lang): string {
  const parts: string[] = [];

  if (imperial && ing.imperial) {
    const qty = ing.imperial.quantity * factor;
    parts.push(formatQty(qty));
    parts.push(qty === 1 ? ing.imperial.singular_unit : ing.imperial.plural_unit);
  } else if (ing.quantity != null) {
    const qty = ing.quantity * factor;
    parts.push(formatQty(qty));
    const unit = qty === 1 ? ing.singular_unit ?? ing.unit : ing.plural_unit ?? ing.unit;
    if (unit) parts.push(unit);
  }

  parts.push(ing.name);
  let line = parts.join(" ");
  if (ing.is_ingredient_optional) line += ` (${T.optional[lang]})`;
  return line;
}

export function RecipeDetail({ slug, lang }: { slug: string; lang: Lang }) {
  const [recipe, setRecipe] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [servings, setServings] = useState(1);
  const [imperial, setImperial] = useState(false);
  const { has, toggle } = useFavorites();
  const mounted = useMounted();

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    setRecipe(null);
    window.scrollTo(0, 0);

    fetch(`/api/recipes/${encodeURIComponent(slug)}?lang=${lang}`, { signal: ctrl.signal })
      .then(res => {
        if (!res.ok) throw new Error(res.status === 404 ? T.notFound[lang] : `HTTP ${res.status}`);
        return res.json() as Promise<Detail>;
      })
      .then(r => {
        setRecipe(r);
        setServings(r.servings_in_num || 1);
      })
      .catch(err => {
        if (err.name !== "AbortError") setError(err.message);
      })
      .finally(() => setLoading(false));

    return () => ctrl.abort();
  }, [slug, lang]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="text-muted-foreground">{error ?? T.notFound[lang]}</p>
        <Button variant="outline" className="mt-6" onClick={() => navigate(langPath(lang))}>
          <ArrowLeft /> {T.back[lang]}
        </Button>
      </div>
    );
  }

  const baseServings = recipe.servings_in_num || 1;
  const factor = servings / baseServings;
  const fav = mounted && has(recipe.id);

  // The other language's slug lets the toggle land on the same recipe.
  const langTargets: Record<Lang, string> = {
    en: langPath("en", recipe.slug_en ?? slug),
    fr: langPath("fr", recipe.slug_fr ?? slug),
  };

  return (
    <article className="min-h-screen w-full pb-20">
      <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-6">
        <a
          href={langPath(lang)}
          onClick={e => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            navigate(langPath(lang));
          }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> {T.back[lang]}
        </a>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={fav ? T.removeFavorite[lang] : T.addFavorite[lang]}
            aria-pressed={fav}
            onClick={() => toggle(recipe.id)}
            className="inline-flex size-9 items-center justify-center rounded-full border transition-colors hover:bg-accent"
          >
            <Heart
              className={`size-4 transition-colors ${fav ? "fill-red-500 stroke-red-500" : "text-foreground/70"}`}
            />
          </button>
          <LangToggle lang={lang} targets={langTargets} />
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4">
        {recipe.video_url ? (
          <HlsVideo
            src={recipe.video_url}
            poster={recipe.video_preview_url ?? recipe.main_image_thumbs.extra_large_landscape}
            className="aspect-video w-full rounded-xl bg-black object-cover"
          />
        ) : (
          <img
            src={recipe.main_image_thumbs.extra_large_landscape}
            alt={recipe.title}
            className="aspect-[2/1] w-full rounded-xl object-cover"
          />
        )}

        <h1 className="mt-6 text-3xl font-bold tracking-tight sm:text-4xl">{recipe.title}</h1>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
          {recipe.total_time != null && (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="size-4" /> {recipe.total_time} {T.minTotal[lang]}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5">
            <Soup className="size-4" /> {recipe.servings_in_char}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Star className="size-4" /> {difficultyLabel(recipe.difficulty, lang)}
          </span>
        </div>

        {(recipe.preparation_time != null || recipe.cooking_time != null || recipe.rest_time != null) && (
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            {recipe.preparation_time != null && (
              <span className="rounded-full bg-secondary px-3 py-1 text-secondary-foreground">
                {T.prep[lang]} {recipe.preparation_time} {T.min[lang]}
              </span>
            )}
            {recipe.cooking_time != null && (
              <span className="rounded-full bg-secondary px-3 py-1 text-secondary-foreground">
                {T.cook[lang]} {recipe.cooking_time} {T.min[lang]}
              </span>
            )}
            {recipe.rest_time != null && (
              <span className="rounded-full bg-secondary px-3 py-1 text-secondary-foreground">
                {T.restLabel[lang]} {recipe.rest_time} {T.min[lang]}
              </span>
            )}
          </div>
        )}

        {recipe.introduction && (
          <div
            className="prose prose-neutral mt-8 max-w-none text-foreground/90 [&_p]:mb-4 [&_p]:leading-relaxed"
            dangerouslySetInnerHTML={{ __html: recipe.introduction }}
          />
        )}

        {/* Ingredients */}
        <section className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-2xl font-semibold">{T.ingredients[lang]}</h2>

            <div className="flex items-center gap-2">
              <div className="inline-flex items-center rounded-md border">
                <button
                  className="px-2.5 py-1.5 hover:bg-accent disabled:opacity-40"
                  disabled={servings <= 1}
                  onClick={() => setServings(s => Math.max(1, s - 1))}
                  aria-label="-"
                >
                  <Minus className="size-4" />
                </button>
                <span className="min-w-16 px-2 text-center text-sm tabular-nums">
                  {servings}{" "}
                  {servings === 1
                    ? (recipe.servings_unit?.singular_name ?? T.servingOne[lang])
                    : (recipe.servings_unit?.plural_name ?? T.servingMany[lang])}
                </span>
                <button className="px-2.5 py-1.5 hover:bg-accent" onClick={() => setServings(s => s + 1)} aria-label="+">
                  <Plus className="size-4" />
                </button>
              </div>

              <div className="inline-flex rounded-md border text-sm">
                <button
                  className={`rounded-l-md px-3 py-1.5 ${!imperial ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
                  onClick={() => setImperial(false)}
                >
                  {T.metric[lang]}
                </button>
                <button
                  className={`rounded-r-md px-3 py-1.5 ${imperial ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
                  onClick={() => setImperial(true)}
                >
                  {T.imperial[lang]}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-6">
            {recipe.compositions.map((comp, i) => (
              <div key={i}>
                {comp.name && <h3 className="mb-2 font-medium">{comp.name}</h3>}
                <ul className="space-y-1.5">
                  {comp.ingredients.map(ing => (
                    <li key={ing.id} className="flex gap-2 border-b border-border/60 pb-1.5 text-sm">
                      <span className="text-muted-foreground">•</span>
                      <span>{ingredientLine(ing, factor, imperial, lang)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Steps */}
        {recipe.steps.length > 0 && (
          <section className="mt-10">
            <h2 className="text-2xl font-semibold">{T.steps[lang]}</h2>
            <ol className="mt-4 space-y-5">
              {recipe.steps.map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                    {i + 1}
                  </span>
                  <p className="pt-1 leading-relaxed">{step}</p>
                </li>
              ))}
            </ol>
          </section>
        )}

        {recipe.notes && (
          <section className="mt-10 rounded-lg bg-muted/60 p-4">
            <h2 className="font-semibold">{T.notes[lang]}</h2>
            <p className="mt-2 text-sm leading-relaxed">{recipe.notes}</p>
          </section>
        )}

        {recipe.tags.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-1.5">
            {recipe.tags.map(tag => (
              <span key={tag.slug} className="rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground">
                {tag.name}
              </span>
            ))}
          </div>
        )}

        <div className="mt-10">
          <a href={recipe.url} target="_blank" rel="noreferrer">
            <Button variant="outline">
              {T.viewOriginal[lang]} <ExternalLink />
            </Button>
          </a>
        </div>
      </div>
    </article>
  );
}
